import { LocalAudioTrack } from "twilio-video";

/*

  Using a FFT on incoming audio samples to display the audio level.

  See documentation for more details:
  https://www.twilio.com/docs/video/build-js-video-application-recommendations-and-best-practices#testing-the-microphone-and-camera

*/

const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
const audioContext = AudioContext ? new AudioContext() : null;

const rootMeanSquare = (samples: Uint8Array): number => {
  const sumSq = samples.reduce((sumSq, sample) => sumSq + sample * sample, 0);
  return Math.sqrt(sumSq / samples.length);
};

const pollAudioLevel = async (
  track: LocalAudioTrack,
  onLevelChanged: (level: number) => void
): Promise<void> => {
  if (!audioContext) {
    return;
  }

  await audioContext.resume();

  const analyser = audioContext.createAnalyser();

  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.5;

  const stream = new MediaStream([track.mediaStreamTrack]);
  const source = audioContext.createMediaStreamSource(stream);
  source.connect(analyser);

  const samples = new Uint8Array(analyser.frequencyBinCount);
  let level: number | null = null;

  requestAnimationFrame(function checkLevel() {
    analyser.getByteFrequencyData(samples);
    const rms = rootMeanSquare(samples);
    const log2Rms = rms && Math.log2(rms);

    const newLevel = Math.ceil((10 * log2Rms) / 8);

    if (level !== newLevel) {
      level = newLevel;
      onLevelChanged(level);
    }

    if (track.mediaStreamTrack.readyState === "live") {
      requestAnimationFrame(checkLevel);
    } else {
      requestAnimationFrame(() => onLevelChanged(0));
    }
  });
};

export default pollAudioLevel;
